import { Button, Dialog, DialogActions, DialogContent, DialogContentText, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useNavigate } from 'react-router-dom';
import { useActiveClassroom, useActiveSimulation } from '../../active-simulation/useActiveSimulation';
import { useProfile } from '../../api';
import { useCheckRole } from '../../auth';
import CreateAdHocExerciseDialog from '../../create-exercise/ad-hoc/CreateAdHocExerciseDialog';
import CreateExerciseDialog from '../../create-exercise/scheduled/CreateExerciseDialog';
import JoinSimulationDialog from '../../JoinSimulationDialog';
import LandingPageLayout from '../../layouts/LandingPageLayout';
import { queryClient } from '../../queryClient';
import ScheduledExercises from '../../scheduled-exercises/ScheduledExercises';
import {
  ExerciseState,
  ExerciseSummary,
  InstructorClassroomSession,
  LoadingSimulation,
  SimulationRole,
} from '../../types';
import { Pages } from '../../types/pages';
import { isAdHoc } from '../../utils';
import { isRunning } from '../../utils/activeSimulation';
import { parseTime } from '../../utils/parseTime';
import { useActiveCompany } from '../../utils/useCompany';

const limit = 16;

const Teach = () => {
  useCheckRole(['Instructor']);
  const navigate = useNavigate();
  const companyId = useActiveCompany()?.companyId;

  const [exerciseState, setExerciseState] = useState(ExerciseState.Active);
  const [showDialog, setShowDialog] = useState(false);

  const [loadingExercises, setLoadingExercises] = useState(true);
  const [exercisesError, setExercisesError] = useState(false);

  const [data, setData] = useState<ExerciseSummary[]>([]);
  const [skip, setSkip] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const { profile } = useProfile();
  const { activeSimulation, setActiveSimulation } = useActiveSimulation();
  const { activeClassroom, setActiveClassroom } = useActiveClassroom();

  const [showCreateAdHocExerciseDialog, setShowCreateAdHocExerciseDialog] = useState(false);

  const [showCreateExerciseDialog, setShowCreateExerciseDialog] = useState(false);
  const [showJoinWithCodeDialog, setShowJoinWithCodeDialog] = useState(false);

  useEffect(() => {
    if (companyId) {
      fetchData();
    }
  }, [companyId, exerciseState]);

  const fetchData = async () => {
    try {
      const response = await queryClient.fetchQuery<ExerciseSummary[]>({
        queryKey: [
          {
            path: `teach/companies/${companyId}/scheduled-exercises?scheduleState=${exerciseState}&limit=${limit}&skip=${skip}`,
          },
        ],
      });

      setLoadingExercises(false);

      if (response.length > 0) {
        // @ts-ignore
        const parseResponse: ExerciseSummary[] = response?.map(parseTime);
        setData(skip === 0 ? parseResponse : data.concat(parseResponse));
        setSkip(skip + limit);

        if (response.length < limit) {
          setHasMore(false);
        }
      } else {
        setHasMore(false);
      }
    } catch (error) {
      setLoadingExercises(false);

      setHasMore(false);
      setExercisesError(true);
    }
  };

  const hasAdHocExercise = data?.find(isAdHoc);

  const onCloseCreateAdHocExerciseDialog = (reason?: string) => {
    if (typeof reason !== 'object' && reason !== 'escapeKeyDown') {
      fetchData();
    }

    if (reason !== 'backdropClick') {
      setShowCreateAdHocExerciseDialog(false);
    }
  };

  const handleConfirmSimulation = (activeSimulation: LoadingSimulation) => {
    setShowCreateAdHocExerciseDialog(false);
    setActiveSimulation(activeSimulation);
  };

  const handleConfirmClassroom = (activeClassroom: InstructorClassroomSession) => {
    setShowCreateAdHocExerciseDialog(false);
    setActiveClassroom(activeClassroom);

    activeClassroom.role === SimulationRole.Instructor &&
      navigate(`./exercises/${activeClassroom.id}/${Pages.Classroom}`);
  };

  const onCloseCreateExerciseDialog = (reason?: string) => {
    if (typeof reason !== 'object' && reason !== 'escapeKeyDown') {
      setHasMore(true);

      fetchData();
    }

    if (reason !== 'backdropClick') {
      setShowCreateExerciseDialog(false);
    }
  };

  return (
    <LandingPageLayout
      actions={
        <>
          <Button
            variant="outlined"
            disabled={isRunning(activeSimulation) || !!activeClassroom?.id}
            onClick={() => setShowJoinWithCodeDialog(true)}
          >
            Join with code
          </Button>
          <Button
            variant="contained"
            disabled={isRunning(activeSimulation) || !!activeClassroom?.id}
            onClick={() => (hasAdHocExercise ? setShowDialog(true) : setShowCreateAdHocExerciseDialog(true))}
          >
            Simulate now
          </Button>
        </>
      }
      scheduledExercises={
        <InfiniteScroll
          dataLength={data.length}
          next={fetchData}
          hasMore={hasMore}
          loader={
            data.length ? (
              <Typography component="h3" variant="h6" gutterBottom>
                Loading...
              </Typography>
            ) : (
              ''
            )
          }
        >
          <ScheduledExercises
            exercises={data}
            exercisesError={exercisesError}
            loadingExercises={loadingExercises}
            onChange={state => {
              setLoadingExercises(true);
              setSkip(0);
              setHasMore(true);
              setData([]);
              setExerciseState(state);
            }}
            onAction={exercise => setData(data.filter(item => item.id !== exercise?.id))}
          />
        </InfiniteScroll>
      }
      secondaryActions={
        <>
          <Button
            variant="contained"
            onClick={() => {
              setSkip(0);
              setShowCreateExerciseDialog(true);
            }}
          >
            Schedule exercise
          </Button>
        </>
      }
    >
      {profile && (
        <>
          <CreateAdHocExerciseDialog
            onClose={onCloseCreateAdHocExerciseDialog}
            onConfirmClassroom={handleConfirmClassroom}
            onConfirmSimulation={handleConfirmSimulation}
            open={showCreateAdHocExerciseDialog}
          />
          <CreateExerciseDialog
            onClose={onCloseCreateExerciseDialog}
            onConfirm={onCloseCreateExerciseDialog}
            open={showCreateExerciseDialog}
          />
          <JoinSimulationDialog showDialog={showJoinWithCodeDialog} reset={() => setShowJoinWithCodeDialog(false)} />
        </>
      )}

      {hasAdHocExercise && (
        <Dialog
          fullWidth
          maxWidth="xs"
          open={showDialog}
          onClose={() => {
            setShowDialog(false);
          }}
        >
          <DialogContent>
            <DialogContentText>
              Want to start another exercise now? Let us close the one you already have open for you first.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'space-between' }}>
            <Button onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button
              onClick={() => {
                setShowDialog(false);
                setShowCreateAdHocExerciseDialog(true);
              }}
              variant="contained"
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </LandingPageLayout>
  );
};

export default Teach;
